import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetOrganizationRoles } from '@/api/auth/roles/roles'
import type { UserRoleResponse } from '@/api/auth/missionImpactAuthAPI.schemas'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { InviteUserDialog } from '@/components/orgs/InviteUserDialog'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { UserPlus } from 'lucide-react'
import { useRoles } from '@/contexts/RolesContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { EditMemberRoleDialog } from '@/components/orgs/EditMemberRoleDialog'
import { DeleteMemberDialog } from '@/components/orgs/DeleteMemberDialog'
import { ReinviteUserDialog } from '@/components/orgs/ReinviteUserDialog'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/components/common/PageHeader'
import { useUser } from '@/contexts/UserContext'
import { isCurrentUserRole } from '@/lib/memberIdentity'

/**
 * Page for managing organization members and their role assignments.
 */
export default function OrganizationMembersPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const { isAdmin } = useRoles()
  const { t, language } = useLanguage()
  const { account, meProfile } = useUser()

  const { data: rolesData, isLoading, error, refetch } = useGetOrganizationRoles(orgId ?? '')
  const roles = rolesData?.data.roles ?? []

  // Log API response to check invitation status
  useEffect(() => {
    if (rolesData?.data.roles) {
      console.log('Organization Roles API Response:', {
        totalRoles: rolesData.data.roles.length,
        roles: rolesData.data.roles.map(role => ({
          email: role.email,
          role: role.role,
          invitationState: role.invitationState,
          invitationExpiresUtc: role.invitationExpiresUtc,
          membershipExpiresUtc: role.membershipExpiresUtc,
          createdUtc: role.createdUtc,
        }))
      });
    }
  }, [rolesData]);

  // Mirrors the auth-service "must keep at least one admin" rule, which
  // counts only Accepted administrator roles (pending invitations excluded).
  const administratorCount = useMemo(
    () => roles.filter((role) => role.role === 'Administrator' && role.invitationState === 'Accepted').length,
    [roles]
  )

  const locale = useMemo(() => {
    switch (language) {
      case 'fr':
        return 'fr-FR'
      case 'de':
        return 'de-DE'
      default:
        return 'en-US'
    }
  }, [language])

  const getRoleBadgeVariant = (role: UserRoleResponse['role']) => {
    switch (role) {
      case 'Administrator':
        return 'default'
      case 'Operator':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  /**
   * Compute the effective status of a member based on invitation state and membership expiration
   */
  const getEffectiveStatus = (role: UserRoleResponse): string => {
    // Check if membership has expired
    if (role.membershipExpiresUtc) {
      const expirationDate = new Date(role.membershipExpiresUtc)
      const now = new Date()
      if (expirationDate < now) {
        return 'Expired'
      }
    }

    // Otherwise use invitation state
    return role.invitationState
  }

  const getEffectiveStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Accepted':
        return 'default'
      case 'Invited':
        return 'secondary'
      case 'Declined':
      case 'Expired':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  /**
   * Determines whether the provided role is the last remaining administrator.
   */
  const isLastRemainingAdministrator = (role: UserRoleResponse) =>
    role.role === 'Administrator' && administratorCount === 1

  /**
   * Determines whether the provided role belongs to the signed-in user. Users can't change their
   * own role or remove themselves; another administrator must do so, which guarantees at least
   * one administrator always remains.
   */
  const isOwnRole = (role: UserRoleResponse) =>
    isCurrentUserRole(role, {
      oid: meProfile?.oid ?? account.localAccountId,
      emails: [meProfile?.email, account.username],
    })

  // Redirect non-admin users to dashboard
  useEffect(() => {
    if (!isAdmin) {
      void navigate('/dashboard')
    }
  }, [isAdmin, navigate])

  if (!orgId) {
    return <div>{t.organizationMembers.organizationNotFound}</div>
  }

  // Don't render content for non-admins
  if (!isAdmin) {
    return null
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t.organizationMembers.title} description={t.organizationMembers.description}>
        <Button onClick={() => { setInviteDialogOpen(true); }}>
          <UserPlus className="mr-2 h-4 w-4" />
          {t.organizationMembers.inviteNewUser}
        </Button>
      </PageHeader>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
        {error ? (
          <div className="p-8">
            <QueryErrorState 
              error={error}
              message={t.errors.members.failedToLoadRoles}
              onRetry={() => { void refetch(); }}
            />
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center text-muted-foreground">{t.organizationMembers.loadingMembers}</div>
        ) : roles.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {t.organizationMembers.noMembersFound}
          </div>
        ) : (
          <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
            <TableHeader>
              <TableRow>
                <TableHead>{t.organizationMembers.table.email}</TableHead>
                <TableHead>{t.organizationMembers.table.role}</TableHead>
                <TableHead>{t.organizationMembers.table.status}</TableHead>
                <TableHead>{t.organizationMembers.table.joined}</TableHead>
                <TableHead>{t.organizationMembers.table.expires}</TableHead>
                <TableHead className="w-28 text-right">
                  {t.organizationMembers.table.actions}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role: UserRoleResponse) => {
                const effectiveStatus = getEffectiveStatus(role)
                return (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(role.role)}>
                        {t.organizationMembers.roles[role.role]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getEffectiveStatusBadgeVariant(effectiveStatus)}>
                        {t.organizationMembers.status[effectiveStatus as keyof typeof t.organizationMembers.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(role.createdUtc).toLocaleDateString(locale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      {role.membershipExpiresUtc
                        ? new Date(role.membershipExpiresUtc).toLocaleDateString(locale, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : t.organizationMembers.indefinite}
                    </TableCell>
                    <TableCell>
                      {!isLastRemainingAdministrator(role) && !isOwnRole(role) ? (
                        <div className="flex justify-end gap-1">
                          {effectiveStatus === 'Declined' && (
                            <ReinviteUserDialog organizationId={orgId} role={role} />
                          )}
                          <EditMemberRoleDialog organizationId={orgId} role={role} />
                          <DeleteMemberDialog organizationId={orgId} role={role} />
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        orgId={orgId}
      />
    </div>
  )
}
